// Chat de suporte flutuante com IA do Dados PJ: a IA lê o código do app no servidor (rota conversar), responde com
// passo a passo em cards, guia na tela e vídeo; o print vai junto da pergunta. Chamado: o servidor do DadosPJ
// abre o caso na Área do Cliente (card com número e link). O guia na tela (GuiaInterativo) monta junto do chat.
import { useEffect, useRef, useState } from "react";
import { authFetch } from "../api";
import { mascararSegredo } from "./texto";
import { guiaValido, midiaValida, passosValidos, urlDoVideo } from "./midia";
import { inicial, passar } from "./fluxo";
import { compactarContexto } from "./contexto";
import { TEXTOS_CHAMADO, abrirChamado as pedirChamado, lerCard, novaChave } from "./chamado";
import CardsDePassos from "./CardsDePassos";
import CardDoChamado from "./CardDoChamado";
import JanelaDoAssistente from "./JanelaDoAssistente";
import BarraDeSaida from "./BarraDeSaida";
import TextoSuporte from "./TextoSuporte";
import PrintAmpliado from "./PrintAmpliado";
import ExemploDoGuia from "./ExemploDoGuia";
import GuiaInterativo from "./GuiaInterativo";
import { aoConcluirGuia, iniciarGuia } from "./guia";
import { Camera, ChevronLeft, ImagePlus, Plus, Pointer, X } from "./icones";
import { capturarTela, imagemColada, reduzirPrint } from "./anexoDePrint";
import { API, abrirConversa, continuavel, criarGravador } from "./conversas";
import { useListaDeConversas } from "./listaDeConversas";
import css from "./SuporteIA.module.css";

// Vídeos já mostrados na conversa: o servidor não repete.
const videosVistos = (l) => l.flatMap((m) => m.midias ?? []).flatMap((x) => (x.tipo === "video" ? [x.id] : []));

// Turno mandado à IA: o print das últimas mensagens vai junto (o servidor limita a 2 imagens).
const paraTurno = (m) => ({ papel: m.papel, texto: m.texto, ...(m.print?.src && { imagem: m.print.src }) });
const textoSalvo = (m) =>
  [
    m.passos ? "[passo a passo] " + m.passos.artigo.titulo : m.texto,
    m.print && "[print]",
    m.cartao && "[chamado #" + m.cartao.codigo + "]",
    ...(m.midias ?? []).map((x) => "[video " + x.id + "]"),
  ]
    .filter(Boolean)
    .join(" ")
    .slice(0, 4000);

const FASES = ["livre", "resolveu", "video", "explicar", "encerrada", "encaminhada"];
// Fases paradas esperando um botão: o servidor encerra a conversa depois de 24h assim.
const ESPERA_BOTAO = new Set(["resolveu", "video"]);
// Estado salvo com o chat; grande demais volta como conversa livre.
const estadoSalvo = (f) => {
  const fluxo = JSON.stringify(f).length < 7000 ? f : { ...inicial, falhas: f.falhas };
  return { ...fluxo, aguardandoBotao: ESPERA_BOTAO.has(fluxo.fase) };
};
const ehFluxo = (f) => !!f && FASES.includes(f.fase) && Array.isArray(f.artigos);
const semMarca = (f) => {
  const resto = { ...f };
  delete resto.aguardandoBotao;
  return resto;
};
// Visual da mensagem guardado com ela, para a conversa retomada ficar igual (o print da pessoa não vai: é pesado).
const visualDe = (m) => {
  const v = { texto: m.texto, midias: m.midias, guia: m.guia, passos: m.passos, cartao: m.cartao };
  return Object.values({ ...v, texto: undefined }).some(Boolean) && JSON.stringify(v).length < 7000 ? v : undefined;
};
function mensagemSalva(m) {
  const v = m.visual && typeof m.visual === "object" ? m.visual : {};
  const passos = passosValidos(v.passos);
  const cartao = v.cartao && lerCard({ numero: v.cartao.codigo, url: v.cartao.href, titulo: v.cartao.titulo });
  return {
    papel: m.papel,
    texto: typeof v.texto === "string" ? v.texto : m.texto,
    ...(Array.isArray(v.midias) && { midias: v.midias.filter(midiaValida) }),
    ...(guiaValido(v.guia) && { guia: v.guia }),
    ...(passos && { passos }),
    ...(cartao && { cartao }),
  };
}
const quando = (iso) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function SuporteIA({ chamadoAtivo = false }) {
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState("");
  // Exemplo animado some na 1ª tecla e só volta em conversa nova.
  const [exemploVisto, setExemploVisto] = useState(false);
  const mudarTexto = (v) => {
    setTexto(v);
    if (v && !exemploVisto) setTimeout(() => setExemploVisto(true), 300);
  };
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [etapa, setEtapa] = useState("buscando");
  const [anexo, setAnexo] = useState(null);
  // true = o print da tela atual veio sozinho ao abrir o chat (não vale como mensagem por si só).
  const [anexoAuto, setAnexoAuto] = useState(false);
  const [capturando, setCapturando] = useState(false);
  const [erroAnexo, setErroAnexo] = useState("");
  const seletor = useRef(null);
  const [fluxo, setFluxo] = useState(inicial);
  const fim = useRef(null);
  // Espelhos síncronos: os efeitos do fluxo leem o estado mais recente, não o do último render.
  const msgs = useRef([]);
  const fluxoRef = useRef(inicial);
  const gravador = useRef(null);
  const [falhouSalvar, setFalhouSalvar] = useState(false);
  const contexto = useRef({ resumo: "", desde: 0 });
  // Chave de idempotência do chamado em curso: a mesma ao tentar de novo, nova a cada pedido.
  const chaveChamado = useRef("");
  // Lista de conversas salvas: abre nela quando já há alguma.
  const [tela, setTela] = useState("lista");
  // Conversa congelada (48h parada): histórico visível, campo desligado.
  const [bloqueio, setBloqueio] = useState(false);

  useEffect(() => {
    fim.current?.scrollIntoView?.({ block: "end" });
  }, [mensagens, carregando, erro, fluxo]);

  const mudar = (fn) => {
    msgs.current = fn(msgs.current);
    setMensagens(msgs.current);
  };

  const conversas = useListaDeConversas(aberto);
  const verLista = tela === "lista" && conversas.lista.length > 0;

  const gravar = () =>
    (gravador.current ??= criarGravador(
      () => setFalhouSalvar(true),
      () => setBloqueio(true),
    ));
  const registrar = (eventos) => gravar().registrar(eventos, estadoSalvo(fluxoRef.current));

  const adicionar = (m, origem) => {
    mudar((l) => [...l, m]);
    const visual = visualDe(m);
    void registrar([{ t: "msg", papel: m.papel, texto: textoSalvo(m), origem, ...(visual && { meta: { visual } }) }]);
  };
  const turnosDesde = (i) =>
    msgs.current
      .slice(i)
      .filter((m) => (m.texto || m.print?.descricao) && !m.escrevendo)
      .map(paraTurno);

  async function anexar(arquivo) {
    if (!arquivo) return;
    setErroAnexo("");
    try {
      setAnexo(await reduzirPrint(arquivo));
      setAnexoAuto(false);
    } catch (e) {
      setAnexo(null);
      setErroAnexo(e instanceof Error ? e.message : "Não consegui abrir essa imagem.");
    }
  }
  const colar = (e) => {
    const img = imagemColada(e.clipboardData);
    if (img) {
      e.preventDefault();
      void anexar(img);
    }
  };

  // Print da tela em que a pessoa está (o chat fica fora da imagem). Falha em silêncio ao abrir sozinho.
  async function capturar(manual) {
    setCapturando(true);
    setErroAnexo("");
    try {
      setAnexo(await capturarTela());
      setAnexoAuto(!manual);
    } catch (e) {
      if (manual) setErroAnexo(e instanceof Error ? e.message : "Não consegui capturar a tela.");
    } finally {
      setCapturando(false);
    }
  }
  const abrirChat = () => {
    setAberto(true);
    void capturar(false);
  };

  // A conversa anterior fica salva na lista; as regras de 24h/48h do servidor a encerram.
  const recomecar = () => {
    setTela("chat");
    setBloqueio(false);
    mudar(() => []);
    fluxoRef.current = inicial;
    setFluxo(inicial);
    contexto.current = { resumo: "", desde: 0 };
    gravador.current = null;
    setFalhouSalvar(false);
    setErro(null);
    setTexto("");
    setExemploVisto(false);
    setAnexo(null);
    setAnexoAuto(false);
    setErroAnexo("");
  };

  async function abrir(item) {
    setErro(null);
    try {
      const d = await abrirConversa(item.id);
      const pode = continuavel(d);
      recomecar();
      mudar(() => d.mensagens.map(mensagemSalva));
      const f = pode ? (ehFluxo(d.estado) ? semMarca(d.estado) : inicial) : { ...inicial, fase: "encerrada" };
      fluxoRef.current = f;
      setFluxo(f);
      contexto.current = { resumo: d.resumo, desde: msgs.current.length };
      if (pode) gravador.current = criarGravador(() => setFalhouSalvar(true), () => setBloqueio(true), d.id);
      setBloqueio(!pode);
    } catch (e) {
      setErro({ msg: e instanceof Error ? e.message : "Não foi possível abrir a conversa.", repetir: () => void abrir(item) });
    }
  }

  const encerrarGravacao = () => void gravar().encerrar(contexto.current.resumo.slice(0, 2000));

  const executar = (ef) => {
    const f = fluxoRef.current;
    if (ef.e === "bot") adicionar({ papel: "suporte", texto: ef.texto }, "fluxo");
    else if (ef.e === "video") {
      if (f.video) adicionar({ papel: "suporte", texto: "", midias: [{ tipo: "video", ...f.video }] }, "fluxo");
    } else if (ef.e === "feedback")
      void registrar([{ t: "feedback", positivo: ef.positivo, origem: ef.origem, artigos: ef.artigos }]);
    else if (ef.e === "compactar") {
      const turnos = turnosDesde(contexto.current.desde);
      contexto.current = { resumo: compactarContexto(turnos, contexto.current.resumo), desde: msgs.current.length };
    } else if (ef.e === "esgotou") encaminhar();
    else encerrarGravacao();
  };

  // IA esgotou: abre o caso direto na Área do Cliente (o servidor envia).
  function encaminhar() {
    if (chamadoAtivo) return void abrirChamado();
    adicionar({ papel: "suporte", texto: TEXTOS_CHAMADO.desligado }, "fluxo");
    encerrarGravacao();
  }

  async function abrirChamado() {
    setErro(null);
    setCarregando(true);
    setEtapa("chamado");
    chaveChamado.current ||= novaChave();
    try {
      const conversa = await gravar().garantir();
      if (!conversa) throw new Error(TEXTOS_CHAMADO.falhou);
      const historico = turnosDesde(0).map(({ papel, texto }) => ({ papel, texto }));
      const { card, repetido } = await pedirChamado({
        conversa,
        chave: chaveChamado.current,
        historico,
        resumo: contexto.current.resumo,
        pagina: window.location.pathname,
      });
      chaveChamado.current = "";
      const f = { ...fluxoRef.current, fase: "encaminhada" };
      fluxoRef.current = f;
      setFluxo(f);
      adicionar({ papel: "suporte", texto: repetido ? TEXTOS_CHAMADO.repetido : TEXTOS_CHAMADO.pronto, cartao: card }, "fluxo");
      encerrarGravacao();
    } catch (e) {
      setErro({ msg: e instanceof Error ? e.message : TEXTOS_CHAMADO.falhou, repetir: () => void abrirChamado() });
    } finally {
      setCarregando(false);
    }
  }

  const aplicar = (ev) => {
    const r = passar(fluxoRef.current, ev);
    fluxoRef.current = r.fluxo;
    setFluxo(r.fluxo);
    r.efeitos.forEach(executar);
  };

  // Guia na tela concluído: o chat reabre, ainda perguntando se resolveu.
  useEffect(() => aoConcluirGuia(() => setAberto(true)));

  // Botões do fluxo: a escolha vira uma mensagem da pessoa antes da resposta.
  const responder = (rotulo, ev) => {
    adicionar({ papel: "pessoa", texto: rotulo });
    aplicar(ev);
  };

  // Provedor fora do ar ou stream cortado: sem saída técnica, segue o encaminhamento de "sem solução".
  function semSaida() {
    adicionar({ papel: "suporte", texto: "Não consegui te responder agora." }, "fluxo");
    aplicar({ t: "semSolucao" });
  }

  function tratarFim(e) {
    if (typeof e.texto !== "string" || !Array.isArray(e.midias)) throw new Error("Resposta inválida.");
    const passos = passosValidos(e.passos);
    const midias = e.midias.filter(midiaValida);
    if (e.texto || (!passos && !midias.length)) adicionar({ papel: "suporte", texto: e.texto, ...(midias.length && { midias }) }, "ia");
    if (passos) adicionar({ papel: "suporte", texto: "", passos, ...(passos.guia && { guia: passos.guia }) }, "ia");
    if (e.encerrar === true) {
      // Abuso reincidente: a conversa fecha e o servidor pausa o chat.
      const f = { ...fluxoRef.current, fase: "encerrada" };
      fluxoRef.current = f;
      setFluxo(f);
      encerrarGravacao();
    } else if (e.semSolucao) aplicar({ t: "semSolucao" });
    else if (e.abrirChamado === true) void abrirChamado();
    else aplicar({ t: "entrega", origem: "ia", artigos: [], resolvivel: !!passos, cartoes: !!passos, video: passos?.video ?? null });
  }

  /** imagem: print da pergunta atual (data URL); vai direto ao modelo, sem etapa de leitura. */
  async function pedir(limpa, historico, imagem) {
    setErro(null);
    setCarregando(true);
    setEtapa("buscando");
    try {
      const r = await authFetch(API + "/conversar/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pergunta: limpa || "Mandei um print da tela.",
          imagem: imagem || undefined,
          historico,
          resumo: contexto.current.resumo,
          videosVistos: videosVistos(msgs.current),
          tela: window.location.pathname,
          conversa: gravador.current?.id() || undefined,
        }),
      });
      if (!r.headers.get("content-type")?.includes("ndjson")) {
        const d = await r.json().catch(() => null);
        if (d?.congelada) {
          setBloqueio(true);
          return;
        }
        // Pausa por abuso ou conversas em sequência: o servidor já escreveu o aviso.
        if (d?.bloqueado && typeof d.erro === "string") {
          adicionar({ papel: "suporte", texto: d.erro }, "fluxo");
          return;
        }
        if (r.status === 502 || r.status === 504) {
          semSaida();
          return;
        }
        throw new Error(d?.erro || "Erro " + r.status);
      }
      const leitor = r.body.pipeThrough(new TextDecoderStream()).getReader();
      let resto = "";
      let terminou = false;
      for (;;) {
        const { value, done } = await leitor.read();
        if (done) break;
        resto += value;
        const linhas = resto.split("\n");
        resto = linhas.pop() ?? "";
        for (const linha of linhas) {
          if (!linha.trim()) continue;
          let e;
          try {
            e = JSON.parse(linha);
          } catch {
            continue;
          }
          if (e.t === "erro") throw new Error(e.erro);
          if (e.t === "fim") {
            terminou = true;
            tratarFim(e);
          }
        }
      }
      if (!terminou) semSaida();
    } catch (e) {
      setErro({
        msg: e instanceof Error ? e.message : "Falha ao falar com o suporte.",
        repetir: () => void pedir(limpa, historico, imagem),
      });
    } finally {
      setCarregando(false);
    }
  }

  function enviar(pergunta) {
    const limpa = mascararSegredo(pergunta.trim());
    if ((!limpa && !(anexo && !anexoAuto)) || carregando || (bloqueio && !verLista)) return;
    // Escrever na lista ou numa conversa encerrada começa outra.
    if (verLista) recomecar();
    setTela("chat");
    const historico = turnosDesde(contexto.current.desde);
    setTexto("");
    if (!anexo) {
      adicionar({ papel: "pessoa", texto: limpa });
      void pedir(limpa, historico);
      return;
    }
    mudar((l) => [...l, { papel: "pessoa", texto: limpa, print: { src: anexo } }]);
    setAnexo(null);
    setAnexoAuto(false);
    setErroAnexo("");
    void pedir(limpa, historico, msgs.current[msgs.current.length - 1].print.src);
  }

  const ultima = mensagens[mensagens.length - 1];
  const botoes =
    fluxo.fase === "resolveu"
      ? [
          ["Sim", { t: "sim" }],
          ["Não", { t: "nao" }],
        ]
      : fluxo.fase === "video"
        ? [
            ["Entendi", { t: "sim" }],
            ["Ainda não entendi...", { t: "ainda" }],
          ]
        : null;

  const conteudo = (
    <>
      <p className={css.bolhaIa}>Olá! Sou o suporte do Dados PJ. Me conte o que você precisa fazer.</p>
      {mensagens.length === 0 && !exemploVisto && !conversas.lista.length && <ExemploDoGuia sumir={!!texto} />}

      {mensagens.map((m, i) =>
        m.escrevendo && !m.texto ? null : (
          <div key={i} className={css.mensagem} aria-busy={m.escrevendo || undefined}>
            {m.papel === "pessoa" ? (
              <>
                {m.print && (
                  <div className={css.printDaPessoa}>
                    <PrintAmpliado src={m.print.src} alt="Print que você enviou" />
                  </div>
                )}
                {m.texto && <p className={css.bolhaPessoa}>{m.texto}</p>}
              </>
            ) : (
              <div className={css.bolhaIa}>
                {m.texto && <TextoSuporte texto={m.texto} />}
                {m.passos && <CardsDePassos passos={m.passos} />}
                {m.cartao && <CardDoChamado card={m.cartao} />}
                {m.escrevendo && <span aria-hidden="true" className={css.cursor} />}
                {m.midias?.filter(midiaValida).map((x) => (
                  <iframe key={x.id} src={urlDoVideo(x.id)} title={x.titulo || "Vídeo"} loading="lazy" allowFullScreen className={css.video} />
                ))}
                {m.guia && (
                  <button
                    type="button"
                    onClick={() => {
                      iniciarGuia(m.guia);
                      setAberto(false);
                    }}
                    className={`${css.botaoNaBolha} ${css.botaoGuia}`}
                  >
                    <Pointer size={16} /> Me mostra na tela
                  </button>
                )}
              </div>
            )}
          </div>
        ),
      )}

      {carregando && !(ultima?.escrevendo && ultima.texto) && (
        <p role="status" className={css.carregandoBolha}>
          <span className={css.carregando}>
            <span aria-hidden="true" className={css.nucleos}>
              <i />
              <i />
              <i />
            </span>
            <span className={css.rotulo}>
              {etapa === "chamado" ? "Preparando o caso…" : "Investigando como as coisas funcionam…"}
            </span>
          </span>
        </p>
      )}
      {conversas.erro && (
        <p role="alert" className={css.alertaErro}>
          {conversas.erro}
        </p>
      )}
      {erro && !carregando && (
        <div role="alert" className={css.erroComBotao}>
          <span>{erro.msg}</span>
          <button type="button" onClick={erro.repetir} className={css.botaoErro}>
            Tentar de novo
          </button>
        </div>
      )}
    </>
  );

  const barraBloqueio = bloqueio && (
    <div role="status" className={css.barraBloqueio}>
      <p className={css.semMargem}>Esta conversa foi encerrada. Para continuar, comece uma nova conversa.</p>
      <button type="button" onClick={recomecar} className={css.botaoCheio}>
        Começar nova conversa
      </button>
    </div>
  );

  const barraDeBotoes = botoes ? (
    <div className={css.barraDeBotoes}>
      {botoes.map(([rotulo, ev]) => (
        <button
          key={rotulo}
          type="button"
          disabled={carregando}
          onClick={() => responder(rotulo, ev)}
          className={rotulo === "Não" || rotulo === "Ainda não entendi..." ? css.botaoRespostaVazado : css.botaoResposta}
        >
          {rotulo}
        </button>
      ))}
    </div>
  ) : fluxo.fase === "encerrada" || fluxo.fase === "encaminhada" ? (
    <BarraDeSaida carregando={carregando} aoRecomecar={recomecar} />
  ) : null;

  const travado = carregando || (bloqueio && !verLista);
  const botaoAnexo = (
    <>
      <input
        ref={seletor}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        tabIndex={-1}
        onChange={(e) => {
          void anexar(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        aria-label="Capturar a tela em que estou"
        title="Capturar a tela em que estou"
        disabled={travado || capturando}
        onClick={() => void capturar(true)}
        className={css.botaoAnexo}
      >
        <Camera size={20} />
      </button>
      <button
        type="button"
        aria-label="Anexar print da tela"
        title="Anexar print da tela"
        disabled={travado}
        onClick={() => seletor.current?.click()}
        className={css.botaoAnexo}
      >
        <ImagePlus size={20} />
      </button>
    </>
  );
  const previa =
    anexo || erroAnexo ? (
      <div className={css.previa}>
        {anexo && (
          <>
            <img src={anexo} alt="Print anexado" loading="eager" className={css.previaImagem} />
            <span className={css.previaTexto}>
              {anexoAuto ? "Print da tela atual anexado. Conte sua dúvida ou remova o print." : "Print anexado. Conte o que aconteceu ou só envie."}
            </span>
            <button
              type="button"
              aria-label="Remover print"
              onClick={() => {
                setAnexo(null);
                setAnexoAuto(false);
              }}
              className={css.previaRemover}
            >
              <X size={16} />
            </button>
          </>
        )}
        {erroAnexo && (
          <span role="alert" className={css.previaErro}>
            {erroAnexo}
          </span>
        )}
      </div>
    ) : null;

  const placeholder = fluxo.fase === "explicar" ? "Conte o que aconteceu…" : "Escreva sua pergunta…";

  const aviso = falhouSalvar && (
    <p role="status" className={css.avisoSalvar}>
      Não foi possível salvar esta conversa.
    </p>
  );

  const lista = (
    <nav aria-label="Suas conversas" className={css.lista}>
      <button type="button" onClick={recomecar} className={css.novaConversa}>
        <Plus size={16} /> Nova conversa
      </button>
      {conversas.lista.map((c) => (
        <button key={c.id} type="button" onClick={() => void abrir(c)} className={css.itemDaLista}>
          <span className={css.itemTexto}>
            <span className={css.itemTitulo}>{c.titulo}</span>
            <span className={css.itemData}>
              {quando(c.atualizada_em)}
              {continuavel(c) ? "" : " · encerrada"}
            </span>
          </span>
        </button>
      ))}
    </nav>
  );

  return (
    <>
      <GuiaInterativo />
      <JanelaDoAssistente
        aberto={aberto}
        aoAbrir={abrirChat}
        aoFechar={() => setAberto(false)}
        grande
        titulo="Suporte Dados PJ"
        subtitulo="Tire suas dúvidas aqui"
        rodape="Respostas geradas por IA."
        placeholder={placeholder}
        fimRef={fim}
        texto={texto}
        aoMudarTexto={mudarTexto}
        aoEnviar={() => enviar(texto)}
        ocupado={carregando || (bloqueio && !verLista)}
        bloqueado={bloqueio && !verLista}
        maxCaracteres={500}
        barra={verLista ? undefined : (barraBloqueio || barraDeBotoes) || undefined}
        antesDoForm={previa}
        anexo={botaoAnexo}
        aoColar={colar}
        enviarSemTexto={!!anexo && !anexoAuto}
        acaoDoCabecalho={
          verLista ? null : conversas.lista.length ? (
            <button
              type="button"
              onClick={() => {
                setTela("lista");
                conversas.recarregar();
              }}
              className={css.acaoCabecalho}
            >
              <ChevronLeft size={16} /> Conversas
            </button>
          ) : (
            mensagens.length > 0 && (
              <button type="button" onClick={recomecar} className={css.acaoCabecalho}>
                Nova conversa
              </button>
            )
          )
        }
      >
        {aviso}
        {verLista ? lista : conteudo}
      </JanelaDoAssistente>
    </>
  );
}
